import React from 'react';
import { Alert as MuiAlert, AlertTitle } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
  CheckCircle,
  Error,
  Warning,
  Info,
} from '@mui/icons-material';
import colors from '../colors';
import spacing from '../spacing';
import typography from '../typography';

/**
 * Alert Component
 * @component
 * @description Displays contextual feedback messages for user actions
 * 
 * @example
 * // Success alert
 * <Alert severity="success" title="Guardado">
 *   Los cambios se guardaron correctamente
 * </Alert>
 * 
 * @example
 * // Error alert with action
 * <Alert 
 *   severity="error" 
 *   title="Error" 
 *   onClose={handleClose}
 * >
 *   No se pudo conectar al servidor
 * </Alert>
 */

// Severity backgrounds/text derive from the active MUI theme (light or dark)
// instead of fixed hex values, so the alert never renders a light pastel
// card with unreadable text against a dark app background (or vice versa).
const StyledAlert = styled(MuiAlert)(({ severity, theme }) => {
  const severityColor = colors[severity]?.main || colors.info.main;

  return {
    borderRadius: spacing.borderRadius.md,
    padding: `${spacing[3]}px ${spacing[4]}px`,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.regular,
    border: `1px solid ${theme.palette.mode === 'dark'
      ? theme.palette[severity]?.dark || theme.palette.divider
      : theme.palette[severity]?.light || theme.palette.divider}`,
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(severityColor, 0.16)
      : alpha(severityColor, 0.12),
    // Text always follows the theme's primary text color so it stays legible
    // against the low-alpha tinted background in both light and dark mode.
    color: theme.palette.text.primary,

    '& .MuiAlert-icon': {
      color: severityColor,
    },

    '& .MuiAlert-message': {
      padding: 0,
      flex: 1,
    },

    '& .MuiAlert-action': {
      paddingTop: 0,
      marginRight: 0,
    },
  };
});

const Alert = ({
  children,
  severity = 'info',
  title,
  variant = 'standard',
  icon,
  onClose,
  action,
  ...props
}) => {
  const iconMapping = {
    success: <CheckCircle fontSize="inherit" />,
    error: <Error fontSize="inherit" />,
    warning: <Warning fontSize="inherit" />,
    info: <Info fontSize="inherit" />,
  };

  return (
    <StyledAlert
      severity={severity}
      variant={variant}
      icon={icon !== undefined ? icon : iconMapping[severity]}
      onClose={onClose}
      action={action}
      {...props}
    >
      {title && <AlertTitle sx={{ fontWeight: typography.fontWeight.semibold }}>{title}</AlertTitle>}
      {children}
    </StyledAlert>
  );
};

export default Alert;
